// SNIGJ - User Profile Card Component with PDF Export
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  User, Mail, Phone, MapPin, Building2, Shield, Calendar, 
  FileText, Briefcase, Award, Printer, Download, 
  Scale, Lock, Hash, Activity
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// Role category colors
const CATEGORY_COLORS = {
  PUBLIC: 'bg-gray-500',
  POLICE: 'bg-blue-600',
  BORDER_CONTROL: 'bg-orange-500',
  TRIBUNAL_PAIX: 'bg-cyan-500',
  PARQUET_GI: 'bg-indigo-500',
  COUR_APPEL: 'bg-purple-500',
  PARQUET_GENERAL: 'bg-violet-500',
  COUR_CASSATION: 'bg-pink-500',
  CONSEIL_ETAT: 'bg-emerald-500',
  COUR_SUPREME: 'bg-amber-600',
  COUR_COMPTES: 'bg-teal-500',
  AUXILIAIRES_JUSTICE: 'bg-cyan-600',
  PRISON: 'bg-red-600',
  ADMINISTRATION: 'bg-yellow-600',
  OVERSIGHT: 'bg-green-600',
  EXECUTIVE: 'bg-amber-700',
  SYSTEM: 'bg-red-700',
  OTHER: 'bg-gray-600'
};

const CLEARANCE_COLORS = {
  PUBLIC: 'bg-green-500',
  RESTRICTED: 'bg-blue-500',
  CONFIDENTIAL: 'bg-yellow-500',
  SECRET: 'bg-orange-500',
  TOP_SECRET: 'bg-red-500'
};

const UserProfileCard = ({ userId, language = 'fr', showStats = true, showActions = true }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    if (userId) {
      loadProfile();
    }
  }, [userId]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement du profil' : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Fiche Profil - ${profile?.user?.name || 'Utilisateur'}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            max-width: 800px; 
            margin: 0 auto;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #1e3a5f; 
            padding-bottom: 20px; 
            margin-bottom: 20px;
          }
          .header h1 { 
            color: #1e3a5f; 
            margin: 0; 
            font-size: 24px;
          }
          .header h2 { 
            color: #666; 
            margin: 5px 0 0; 
            font-size: 14px;
            font-weight: normal;
          }
          .photo-section {
            text-align: center;
            margin-bottom: 20px;
          }
          .photo {
            width: 120px;
            height: 120px;
            border-radius: 50%;
            border: 3px solid #1e3a5f;
            object-fit: cover;
          }
          .section { 
            margin-bottom: 20px; 
            page-break-inside: avoid;
          }
          .section-title { 
            background: #1e3a5f; 
            color: white; 
            padding: 8px 15px; 
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .info-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 10px;
          }
          .info-item { 
            padding: 8px; 
            background: #f5f5f5; 
            border-left: 3px solid #1e3a5f;
          }
          .info-label { 
            font-size: 11px; 
            color: #666; 
            text-transform: uppercase;
          }
          .info-value { 
            font-size: 14px; 
            color: #333; 
            font-weight: 500;
          }
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }
          .stat-item {
            text-align: center;
            padding: 15px;
            background: #f0f7ff;
            border: 1px solid #1e3a5f;
          }
          .stat-value {
            font-size: 24px;
            font-weight: bold;
            color: #1e3a5f;
          }
          .stat-label {
            font-size: 11px;
            color: #666;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ccc;
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            color: #666;
          }
          .badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            color: white;
          }
          .badge-blue { background: #1e40af; }
          .badge-green { background: #166534; }
          .badge-orange { background: #c2410c; }
          .badge-red { background: #dc2626; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</h1>
          <h2>Ministère de la Justice - SNIGJ</h2>
          <h2 style="margin-top: 10px; font-size: 18px; color: #1e3a5f;">FICHE DE PROFIL UTILISATEUR</h2>
        </div>
        
        <div class="photo-section">
          ${profile?.user?.picture ? 
            `<img src="${profile.user.picture}" alt="Photo" class="photo" />` : 
            `<div style="width: 120px; height: 120px; border-radius: 50%; border: 3px solid #1e3a5f; background: #e5e5e5; margin: 0 auto; display: flex; align-items: center; justify-content: center; font-size: 48px; color: #666;">${(profile?.user?.name || '?')[0].toUpperCase()}</div>`
          }
        </div>

        <div class="section">
          <div class="section-title">IDENTITÉ</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Nom complet</div>
              <div class="info-value">${profile?.user?.name || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Email</div>
              <div class="info-value">${profile?.user?.email || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Téléphone</div>
              <div class="info-value">${profile?.user?.phone_number || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Carte d'identité</div>
              <div class="info-value">${profile?.user?.national_id || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Date de naissance</div>
              <div class="info-value">${profile?.user?.date_of_birth || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Adresse</div>
              <div class="info-value">${profile?.user?.address || '-'}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">INFORMATIONS PROFESSIONNELLES</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Rôle</div>
              <div class="info-value">${profile?.user?.role || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Grade</div>
              <div class="info-value">${profile?.grade_info?.label_fr || profile?.user?.grade || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Matricule</div>
              <div class="info-value">${profile?.user?.matricule || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Juridiction</div>
              <div class="info-value">${profile?.user?.jurisdiction || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Structure</div>
              <div class="info-value">${profile?.structure_info?.name || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Niveau d'accès</div>
              <div class="info-value">${profile?.user?.clearance_level || 'PUBLIC'}</div>
            </div>
          </div>
        </div>

        ${showStats ? `
        <div class="section">
          <div class="section-title">STATISTIQUES D'ACTIVITÉ</div>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-value">${profile?.statistics?.cases_created || 0}</div>
              <div class="stat-label">Dossiers créés</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${profile?.statistics?.cases_assigned || 0}</div>
              <div class="stat-label">Dossiers assignés</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${profile?.statistics?.complaints_filed || 0}</div>
              <div class="stat-label">Plaintes déposées</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${profile?.statistics?.documents_signed || 0}</div>
              <div class="stat-label">Documents signés</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${profile?.statistics?.prisoners_registered || 0}</div>
              <div class="stat-label">Détenus enregistrés</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">${profile?.user?.is_active ? 'Actif' : 'Inactif'}</div>
              <div class="stat-label">Statut du compte</div>
            </div>
          </div>
        </div>
        ` : ''}

        <div class="footer">
          <div>
            <strong>Date de création du compte:</strong> ${profile?.statistics?.account_created ? new Date(profile.statistics.account_created).toLocaleDateString('fr-FR') : '-'}
          </div>
          <div>
            <strong>Document généré le:</strong> ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
          </div>
        </div>

        <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #999;">
          Document généré par SNIGJ - Système National Intégré de Gestion de la Justice
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-20 w-20 bg-gray-200 rounded-full mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          {language === 'fr' ? 'Profil non trouvé' : 'Profile not found'}
        </CardContent>
      </Card>
    );
  }

  const { user, statistics, grade_info, structure_info, role_category } = profile;
  const categoryColor = CATEGORY_COLORS[role_category] || CATEGORY_COLORS.OTHER;
  const clearanceColor = CLEARANCE_COLORS[user.clearance_level] || CLEARANCE_COLORS.PUBLIC;

  return (
    <Card className="overflow-hidden" ref={printRef}>
      <CardHeader className={`${categoryColor} text-white`}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {language === 'fr' ? 'Fiche Profil' : 'Profile Card'}
          </CardTitle>
          {showActions && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handlePrint}
              className="text-white hover:bg-white/20"
            >
              <Printer className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Imprimer' : 'Print'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Photo and Name */}
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-16 h-16 rounded-full object-cover border-2 border-primary"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-500">
              {(user.name || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="font-bold text-lg">{user.name}</h3>
            <Badge className={categoryColor}>{user.role}</Badge>
          </div>
        </div>

        <Separator />

        {/* Identity Info */}
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
            <User className="w-4 h-4" />
            {language === 'fr' ? 'Identité' : 'Identity'}
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3 text-muted-foreground" />
              <span className="truncate">{user.email}</span>
            </div>
            {user.phone_number && (
              <div className="flex items-center gap-2">
                <Phone className="w-3 h-3 text-muted-foreground" />
                <span>{user.phone_number}</span>
              </div>
            )}
            {user.national_id && (
              <div className="flex items-center gap-2">
                <FileText className="w-3 h-3 text-muted-foreground" />
                <span>{user.national_id}</span>
              </div>
            )}
            {user.date_of_birth && (
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3 text-muted-foreground" />
                <span>{user.date_of_birth}</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Professional Info */}
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            {language === 'fr' ? 'Professionnel' : 'Professional'}
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {(grade_info || user.grade) && (
              <div className="flex items-center gap-2">
                <Award className="w-3 h-3 text-muted-foreground" />
                <span>{grade_info?.label_fr || user.grade}</span>
              </div>
            )}
            {user.matricule && (
              <div className="flex items-center gap-2">
                <Hash className="w-3 h-3 text-muted-foreground" />
                <span>{user.matricule}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3 text-muted-foreground" />
              <span>{user.jurisdiction}</span>
            </div>
            {structure_info && (
              <div className="flex items-center gap-2 col-span-2">
                <Building2 className="w-3 h-3 text-muted-foreground" />
                <span>{structure_info.name}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Lock className="w-3 h-3 text-muted-foreground" />
              <Badge className={`${clearanceColor} text-xs`}>{user.clearance_level}</Badge>
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && statistics && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {language === 'fr' ? 'Statistiques' : 'Statistics'}
              </h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <div className="text-lg font-bold text-blue-600">{statistics.cases_created || 0}</div>
                  <div className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Dossiers' : 'Cases'}
                  </div>
                </div>
                <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <div className="text-lg font-bold text-green-600">{statistics.documents_signed || 0}</div>
                  <div className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Documents' : 'Documents'}
                  </div>
                </div>
                <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <div className="text-lg font-bold text-purple-600">{statistics.complaints_filed || 0}</div>
                  <div className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Plaintes' : 'Complaints'}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Account Status */}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-muted-foreground">
            {language === 'fr' ? 'Créé le' : 'Created'}: {statistics?.account_created ? new Date(statistics.account_created).toLocaleDateString('fr-FR') : '-'}
          </span>
          <Badge variant={user.is_active ? 'default' : 'destructive'}>
            {user.is_active ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Inactif' : 'Inactive')}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserProfileCard;
